import React, { useState, useEffect } from 'react';
import { useWebSocket } from '../../hooks/useWebSocket';

const FeedStatusDisplay = ({ jobId, onComplete }) => {
  const [feedStatus, setFeedStatus] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  
  // Subscribe to job-specific topic if jobId is provided, otherwise general topic
  const topic = jobId ? `/topic/feed-status/${jobId}` : '/topic/feed-status';
  const { lastMessage, connected, error } = useWebSocket(topic);

  useEffect(() => {
    if (lastMessage) {
      setFeedStatus(lastMessage);
      setStatusHistory(prev => [...prev, lastMessage]);
      
      // Call onComplete when process is complete
      if (lastMessage.isComplete && onComplete) {
        onComplete(lastMessage);
      }
    }
  }, [lastMessage, onComplete]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'STARTED':
        return 'text-blue-600';
      case 'FETCHING':
        return 'text-yellow-600';
      case 'BUILDING':
        return 'text-orange-600';
      case 'SUBMITTING':
        return 'text-purple-600';
      case 'COMPLETED':
        return 'text-green-600';
      case 'ERROR':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getProgressPercentage = () => {
    if (!feedStatus || !feedStatus.total || feedStatus.total === 0) return 0;
    return Math.round((feedStatus.current / feedStatus.total) * 100);
  };

  if (!connected && !error) {
    return (
      <div className="mt-4 p-4 bg-gray-100 rounded-lg">
        <p className="text-gray-600">Connecting to status updates...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-4 p-4 bg-red-50 rounded-lg">
        <p className="text-red-600">Connection error. Status updates unavailable.</p>
      </div>
    );
  }

  if (!feedStatus) {
    return (
      <div className="mt-4 p-4 bg-gray-100 rounded-lg">
        <p className="text-gray-600">Waiting for status updates...</p>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      {/* Current Status */}
      <div className="p-4 bg-white border rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-medium">Feed Submission Status</h3>
          {feedStatus.feedId && (
            <span className="text-sm text-gray-500">Feed ID: {feedStatus.feedId}</span>
          )}
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium">Status:</span>
            <span className={`text-sm font-semibold ${getStatusColor(feedStatus.status)}`}>
              {feedStatus.status}
            </span>
          </div>
          
          <div className="text-sm text-gray-700">{feedStatus.message}</div>
          
          {feedStatus.total > 0 && (
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span>Progress: {feedStatus.current} / {feedStatus.total}</span>
                <span>{getProgressPercentage()}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage()}%` }}
                />
              </div>
            </div>
          )}
          
          {feedStatus.isComplete && (
            <div className={`mt-2 p-2 rounded ${feedStatus.hasError ? 'bg-red-50' : 'bg-green-50'}`}>
              <p className={`text-sm ${feedStatus.hasError ? 'text-red-700' : 'text-green-700'}`}>
                {feedStatus.hasError ? 'Process completed with errors' : 'Process completed successfully'}
              </p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default FeedStatusDisplay;